// Packages
import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { FormattedMessage } from 'react-intl';
// Components
import MobileInlineLoader from "../mobileInlineLoader";
// Redux Operations
import { loyaltyOperations } from "../../state/features/loyalty";
import { cartOperations } from "../../state/features/cart";
import { loaderOperations } from "../../state/features/loader";
// Helpers, Utils etc.
import {
  calculateAmountsAndBreakdowns,
  formatDecimal,
} from "../../helpers/itemCalculations";
import { combineCarts } from "../../features/promotions/helpers/utils";

interface ILoyaltyWidgetMobileProps {
  loyalty: any;
  cart: any;
  currency: string;
  menu: any;
  orderType: any;
  discount: any;
  user: any;
  selectedArea: any;
  deliveryChargeId: any;
  fetchLoyalty: any;
  redeemLoyalty: any;
  removeLoyalty: any;
  setCart: any;
  showLoader: any;
  promotionAppliedCart: any[];
  appliedOrderPromotion: any[];
  freeItemsCart: any[];
}

interface ILoyaltyWidgetMobileState {
  pointsToRedeem: any;
  redeemError: any;
  fetchError: any;
  showLoyaltyInput: boolean;
  loyaltyLoading: boolean;
}

class LoyaltyWidgetMobile extends Component<
  ILoyaltyWidgetMobileProps,
  ILoyaltyWidgetMobileState
> {
  constructor(props: ILoyaltyWidgetMobileProps) {
    super(props);
    this.state = {
      pointsToRedeem: "",
      redeemError: null,
      fetchError: null,
      showLoyaltyInput: false,
      loyaltyLoading: true,
    };
  }

  componentDidMount() {
    if (this.props.user.id) {
      this.props.fetchLoyalty().then((response: any) => {
        if (response.error) {
          this.setState({
            fetchError: "We're unable to fetch your loyalty points.",
          });
        }
        this.setState({ loyaltyLoading: false });
      });
    } else {
      this.setState({ loyaltyLoading: false });
    }
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    if (prevProps.loyalty !== this.props.loyalty) {
      this.updateCart();
    }
    if (
      prevProps.cart.totalBeforeLoyalty !== this.props.cart.totalBeforeLoyalty
        && this.props.loyalty?.hasRedeemed
    ) {
      setTimeout(() => {
        this.redeemLoyalty();
      }, 100);
    }
  }

  onLoyaltyPointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!isNaN(value) && parseFloat(value) >= 0 || value == "") {
      this.setState({
        redeemError: null,
        pointsToRedeem: value,
      });
    } else {
      this.setState({
        redeemError: this.loyaltyErrorMessage("invalid_amount", formatDecimal(value)),
        pointsToRedeem: "",
      });
    }
    
  };

  removeLoyalty = () => {
    this.setState(
      {
        redeemError: null,
        pointsToRedeem: "",
      },
      () => this.props.removeLoyalty()
    );
  };

  redeemLoyalty = () => {
    const userLoyalty = this.props.loyalty;
    const pointsToRedeem = parseFloat(this.state.pointsToRedeem || 0);
    const availableLoyalty = parseFloat(userLoyalty.customer_loyalty || 0);
    const minRedeemPoints = parseFloat(userLoyalty.min_redeem_value || 0);
    const maxRedeemPoints = parseFloat(userLoyalty.max_redeem_value || 0);

    if (pointsToRedeem > availableLoyalty) {
      this.setState(
        {
          redeemError: this.loyaltyErrorMessage("insufficient_points", formatDecimal(availableLoyalty)),
        },
        () => this.props.removeLoyalty()
      );
      return;
    } else if (maxRedeemPoints != 0 && pointsToRedeem > maxRedeemPoints) {
      this.setState(
        {
          redeemError: this.loyaltyErrorMessage("above_max_points_for_an_order", formatDecimal(maxRedeemPoints)),
        },
        () => this.props.removeLoyalty()
      );
      return;
    } else if (
      pointsToRedeem > parseFloat(this.props.cart.totalBeforeLoyalty)
    ) {
      this.setState(
        {
          redeemError: this.loyaltyErrorMessage("above_max_points_for_this_order", formatDecimal(this.props.cart.totalBeforeLoyalty)),
        },
        () => this.props.removeLoyalty()
      );
      return;
    } else if (availableLoyalty < minRedeemPoints) {
      this.setState(
        {
          redeemError: this.loyaltyErrorMessage("below_min_points", formatDecimal(minRedeemPoints)),
        },
        () => this.props.removeLoyalty()
      );
      return;
    } else {
      this.setState(
        {
          redeemError: null,
        },
        () => this.props.redeemLoyalty(pointsToRedeem)
      );
    }
  };

  loyaltyErrorMessage = (type: string, loyaltyPoints: any) => {
    switch (type) {
      case "insufficient_points":
        return (
          <FormattedMessage
            id="loyalty.validation.only_have_some_loyalty"
            defaultMessage="You only have {loyaltyPoints} Loyalty {currency} available."
            values={{
              loyaltyPoints: loyaltyPoints,
              currency: this.props.currency,
            }}
          />
        );
      case "above_max_points_for_an_order":
        return (
          <FormattedMessage
            id="loyalty.validation.only_redeem_upto_for_an_order"
            defaultMessage="You can only redeem upto {loyaltyPoints} Loyalty {currency} for an order."
            values={{
              loyaltyPoints: loyaltyPoints,
              currency: this.props.currency,
            }}
          />
        );
      case "above_max_points_for_this_order":
        return (
          <FormattedMessage
            id="loyalty.validation.only_redeem_upto_for_this_order"
            defaultMessage="You can only redeem upto {loyaltyPoints} Loyalty {currency} for this order."
            values={{
              loyaltyPoints: loyaltyPoints,
              currency: this.props.currency,
            }}
          />
        );
      case "below_min_points":
        return (
          <FormattedMessage
            id="loyalty.validation.need_minimum_loyalty"
            defaultMessage="You need a minimum of {loyaltyPoints} Loyalty {currency} to redeem for this order."
            values={{
              loyaltyPoints: loyaltyPoints,
              currency: this.props.currency,
            }}
          />
        );
        case "invalid_amount": // New error type for invalid input
        return (
          <FormattedMessage
            id="loyalty.validation.invalid_amount"
            defaultMessage="Please enter a valid amount."
          />
        );
      default: return "";
    };
  }

  updateCart = () => {
    const currentUserCart = combineCarts(
      this.props.promotionAppliedCart,
      this.props.freeItemsCart
    );
    let cart = calculateAmountsAndBreakdowns(
      currentUserCart,
      this.props.menu,
      this.props.orderType,
      this.props.discount,
      this.props.loyalty.redeemedPoints,
      this.props.selectedArea,
      this.props.deliveryChargeId,
      0,
      this.props.appliedOrderPromotion
    ) as any;
    cart.items = this.props.cart.items;

    this.props.setCart(cart);
  };

  showLoyaltyInputSection = () => {
    this.setState({ showLoyaltyInput: true });
  };

  proceedToCheckout = (e: React.MouseEvent<HTMLAnchorElement>) => {
    this.props.showLoader();
    localStorage.setItem("postRegistrationPage", "/m/order-details");
  };

  render() {
    const {
      pointsToRedeem,
      redeemError,
      fetchError,
      showLoyaltyInput,
      loyaltyLoading,
    } = this.state;
    const { loyalty, currency, user } = this.props;
    return (
      <div>
        {loyaltyLoading ? (
          <div className="py-4">
            <MobileInlineLoader />
          </div>
        ) : fetchError ? (
          <div className="widget-mobile discount-widget-mobile">
            <div className="apply-coupon-section">
              <div className="apply-coupon">
                <div className="enter-coupon">
                  <FormattedMessage
                    id="loyalty.unable_to_retrieve"
                    defaultMessage="We're unable to fetch your loyalty points."
                  />
                </div>
              </div>
            </div>
          </div>
        ) : loyalty.redeemedPoints ? (
          <div className="widget-mobile discount-widget-mobile">
            <div className="apply-coupon-section">
              <div className="apply-coupon">
                <p className="input-label mb-0">
                  <FormattedMessage
                    id="loyalty.you_have_redeemed"
                    defaultMessage="You have redeemed <spanTag>{currency} {redeemedPoints}</spanTag>"
                    values={{
                      currency: currency,
                      redeemedPoints: formatDecimal(loyalty.redeemedPoints),
                      spanTag: (text) => <span  className="loyalty-ponits">{text}</span>,
                    }}
                  />
                </p>
                <p className="enter-coupon" onClick={this.removeLoyalty}>
                  <FormattedMessage
                    id="loyalty.remove_loyalty"
                    defaultMessage="Remove Loyalty"
                  />
                </p>
              </div>
            </div>
          </div>
        ) : user.id ? (
          <div className="widget-mobile discount-widget-mobile">
            <div className="apply-coupon-section">
              <div className="apply-coupon">
                <p
                  className={
                    showLoyaltyInput ? "input-label mb-1" : "input-label mb-0"
                  }
                >
                  <FormattedMessage
                    id="global.loyalty_earnings"
                    defaultMessage="Loyalty Earnings: <spanTag>{amount} loyalty {currency}</spanTag>"
                    values={{
                      amount: formatDecimal(loyalty.customer_loyalty || 0),
                      currency: currency,
                      spanTag:(text) => <span className="loyalty-ponits">{text}</span>,
                    }}
                  />
                </p>
                {!showLoyaltyInput && (
                  <p
                    className="enter-coupon"
                    onClick={this.showLoyaltyInputSection}
                  >
                    <FormattedMessage
                      id="loyalty.redeem"
                      defaultMessage="Redeem"
                    />
                  </p>
                )}
              </div>
              {showLoyaltyInput && (
                <div className="form-group d-flex mb-0">
                  <FormattedMessage
                    id="loyalty.amount_to_redeem"
                    defaultMessage="Amount to redeem"
                  >
                    {(placeholder) => (
                      <input
                        name="loyalty_points"
                        type="text"
                        required
                        className="form-control custom-input mr-2"
                        placeholder={placeholder}
                        autoComplete="off"
                        value={pointsToRedeem}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          this.onLoyaltyPointsChange(e)
                        }
                        onFocus={(e: React.ChangeEvent<HTMLInputElement>) =>
                          this.onLoyaltyPointsChange(e)
                        }
                      ></input>
                    )}
                  </FormattedMessage>
                  <button
                    className="btn btn-outline-primary px-4"
                    onClick={this.redeemLoyalty}
                    disabled={!pointsToRedeem || redeemError || pointsToRedeem==0}
                  >
                    <FormattedMessage
                      id="loyalty.redeem"
                      defaultMessage="Redeem"
                    />
                  </button>
                </div>
              )}
              {redeemError ? (
                <strong className="input-label mt-3 mb-0 text-danger">{redeemError}</strong>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="widget-mobile discount-widget-mobile">
            <div className="apply-coupon-section">
              <div className="apply-coupon">
                <p className="input-label mb-0">
                  <FormattedMessage
                    id="global.loyalty_earnings_text"
                    defaultMessage="Loyalty Earnings"
                  />
                </p>
                <Link
                  to="/m/login"
                  className="enter-coupon"
                  onClick={this.proceedToCheckout}
                >
                  <FormattedMessage
                    id="loyalty.log_in_to_check_points"
                    defaultMessage="Log-in to check points"
                  />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => {
  let loyalty = state.loyalty || {};
  let cart = state.cart;
  let currency = state.company.currency;
  let menu = state.menu;
  let orderType = state.session.order_type;
  let discount = state.discount;
  let user = state.user;
  let selectedArea = state.areas.selectedArea;
  let deliveryChargeId = state.outlet.delivery_charge_id;
  let promotionAppliedCart = state.promotions.promotionAppliedCart;
  let appliedOrderPromotion = state.promotions.appliedOrderPromotion;
  let freeItemsCart = state.promotions.freeItemsCart;

  return {
    loyalty,
    cart,
    currency,
    menu,
    orderType,
    discount,
    user,
    selectedArea,
    deliveryChargeId,
    promotionAppliedCart,
    appliedOrderPromotion,
    freeItemsCart
  };
};

const mapDispatchToProps = {
  fetchLoyalty: loyaltyOperations.fetchLoyalty,
  redeemLoyalty: loyaltyOperations.redeemLoyalty,
  removeLoyalty: loyaltyOperations.removeLoyalty,
  setCart: cartOperations.setCart,
  showLoader: loaderOperations.showLoader,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LoyaltyWidgetMobile);
